import { useState } from "react";
import { format } from "date-fns";
import { Download, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const fmtTokens = (t) => (t < 1000 ? t : `${Math.round(t / 1000)}k`);

const ZipButton = ({ projectId, number }) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/studio/projects/${projectId}/versions/${number}/zip`, { responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || `site-v${number}.zip`;
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(r.data), download: name });
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={download} disabled={busy} data-testid={`studio-zip-${number}`} className="inline-flex shrink-0 items-center gap-1 text-slate-400 hover:text-cyan-300 disabled:opacity-40">
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} Zip
    </button>
  );
};

export const VersionsPanel = ({ project, versions, running, onRestore }) => (
  <div data-testid="studio-versions" className="card-tech rounded-2xl p-4">
    <p className="mb-2 text-sm font-semibold text-white">Versions</p>
    <ul className="space-y-2">
      {versions.map((v) => (
        <li key={v.number} data-testid={`studio-version-${v.number}`} className="flex items-start justify-between gap-2 text-xs">
          <span className="min-w-0">
            <span className="font-mono-tech text-cyan-300">v{v.number}</span>{" "}
            <span className="text-slate-500">{format(new Date(v.created_at), "MMM d, h:mm a")}{v.seconds ? ` · ${v.seconds}s` : ""}{v.approx_tokens ? ` · ~${fmtTokens(v.approx_tokens)} tok` : ""}</span>
            {project.published?.version === v.number && <span className="ml-1 rounded-full bg-emerald-400/10 px-1.5 text-emerald-300">live</span>}
            <span className="block break-words text-slate-300">{v.instruction}</span>
          </span>
          <span className="flex shrink-0 items-center gap-3">
            <ZipButton projectId={project.id} number={v.number} />
            {v.number === project.current_version ? <span className="text-emerald-300">current</span>
              : <button disabled={running} onClick={() => onRestore(v.number)} data-testid={`studio-restore-${v.number}`} className="inline-flex items-center gap-1 text-cyan-300 hover:text-white disabled:opacity-40"><RotateCcw className="h-3 w-3" /> Restore</button>}
          </span>
        </li>
      ))}
    </ul>
  </div>
);
